import { memoize, map } from 'lodash-es';
import axios, { AxiosError } from 'axios';
import React, { useCallback, useState } from 'react';
import Phoneinput from 'react-phone-number-input/input';
import { LayoutTitle, LayoutFooter } from './Layout';
import { preventDefault, getErrorMessagesFromResponse } from '../../../common/utils';
import { BUSINESS_ACCOUNT_TYPE } from '../../../common/types';
import { countries } from '../../../common/countries';
import { industries } from '../../../common/industries';
import { states } from '../../../common/states';
import Button from '../../../components/Button';
import FormGroup from '../../../components/FormGroup';
import { Alert } from '../../../components/Alert';
import { updateHubspotContact } from '..';
export function Details({ fields, updateFields, previousSection, nextSection, onLoading }) {
    const [title] = useState(`Welcome, ${fields.firstName}!`);
    const [description] = useState(fields.accountType === BUSINESS_ACCOUNT_TYPE ?
        'Your account has been verified. Now tell us about your company.' :
        'Your account has been verified. Now tell us about yourself.');
    const [errors, setErrors] = useState([]);
    const updateField = useCallback(memoize((field) => (event) => {
        updateFields({ [field]: event.target.value });
    }), [updateFields]);
    const updatePhone = useCallback((phone) => {
        updateFields({ phone });
    }, [updateFields]);
    const onSubmit = useCallback(preventDefault(async () => {
        onLoading(true);
        const { hsCompanyId, hsContactId } = await updateHubspotContact(fields);
        updateFields({ hsCompanyId, hsContactId });
        try {
            onLoading(true);
            await axios.post('/api/register/validate-details', {
                account_type: fields.accountType,
                company: fields.company,
                phone: fields.phone,
                address: fields.address,
                country: fields.country,
                state: fields.state,
                city: fields.city,
                zipcode: fields.zipcode,
                industry: fields.industry,
            });
            setErrors([]);
            nextSection();
        }
        catch (error) {
            if (error instanceof AxiosError) {
                setErrors(getErrorMessagesFromResponse(error));
            }
            else if (error instanceof Error) {
                setErrors([error.message]);
            }
            else {
                setErrors(['Unknown Error']);
            }
            window.scrollTo(0, 300);
            throw error;
        }
        finally {
            onLoading(false);
        }
    }), [fields, setErrors, onLoading, nextSection, updateFields, updateHubspotContact]);
    return (React.createElement("div", { className: 'register-setup' },
        React.createElement(LayoutTitle, { text: title }),
        React.createElement("p", { className: 'register-setup__description', dangerouslySetInnerHTML: { __html: description } }),
        React.createElement("div", { className: "register-setup__form-wrapper" },
            React.createElement("div", { className: 'register-setup__emailSetup' },
                React.createElement(Alert, { messages: errors, type: "error" }),
                React.createElement("form", { onSubmit: onSubmit },
                    fields.accountType === BUSINESS_ACCOUNT_TYPE && (React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Company Name",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", minLength: 2, maxLength: 100, required: true, value: fields.company, onChange: updateField('company'), name: "company" })))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                fields.accountType === BUSINESS_ACCOUNT_TYPE ? 'Company Phone' : 'Phone',
                                React.createElement("span", { className: "form__required" })),
                            React.createElement(Phoneinput, { defaultCountry: "US", type: "text", inputMode: "tel", className: "form__control", autoComplete: "nope", maxLength: 20, required: true, value: fields.phone, onChange: updatePhone }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                fields.accountType === BUSINESS_ACCOUNT_TYPE ? 'Company Address' : 'Address',
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", minLength: 2, maxLength: 100, required: true, value: fields.address, onChange: updateField('address'), name: "address" }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Country",
                                React.createElement("span", { className: 'form__required' })),
                            React.createElement("select", { value: fields.country, onChange: updateField('country'), className: 'form__control', required: true }, map(countries, country => React.createElement("option", { key: country.code, value: country.code }, country.name))))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "City",
                                React.createElement("span", { className: 'form__required' })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", maxLength: 100, required: true, value: fields.city, onChange: updateField('city'), name: "city" }))),
                    React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "State",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("select", { value: fields.state, onChange: updateField('state'), className: 'form__control', required: true }, map(states, state => React.createElement("option", { key: state.code, value: state.code }, state.name)))),
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Zip Code",
                                React.createElement("span", { className: "form__required" })),
                            React.createElement("input", { type: "text", className: "form__control", placeholder: "", maxLength: 9, required: true, value: fields.zipcode, onChange: updateField('zipcode'), name: "zipcode" }))),
                    fields.accountType === BUSINESS_ACCOUNT_TYPE && (React.createElement("div", { className: "form__row" },
                        React.createElement(FormGroup, null,
                            React.createElement("label", null,
                                "Industry",
                                React.createElement("span", { className: 'form__required' })),
                            React.createElement("select", { value: fields.industry, onChange: updateField('industry'), className: 'form__control', required: true },
                                React.createElement("option", { value: "", disabled: true }, "Select"),
                                map(industries, industry => React.createElement("option", { key: industry.code, value: industry.code }, industry.name)))))),
                    React.createElement(LayoutFooter, { positionShouldChange: true },
                        React.createElement(Button, { variant: "marketing-border", type: 'button', onClick: previousSection }, "Go Back"),
                        React.createElement("input", { className: 'button button--marketing-solid', type: "submit", value: 'Review' })))))));
}
